import { Product } from "src/api/client-api/products/useGetOneProduct";
import { ScoreService } from "src/services/ScoreService";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { useProductSummaryData } from "src/pages/Product/ProductDetails/useProductSummaryData";
import { ProductScoresContext, ProductScoresContextInitialValues } from "./context";
import { useEntityScoresOrUnavailable } from "src/context/Scores/useEntityScores";
import { useEffect } from "react";
import { useOlapScoresParams } from "src/context/OlapScores/useOlapScoresParams";
import { useGetProductScores } from "src/api/client-api/olapScores/useGetProductScores";
import { Dimensions } from "src/context/OlapScores/useDimensions";
import { Loading } from "src/components/utils/Loading";
import { SsqVersionSsq } from "src/api/sure-api/ssq/useGetOneSsqVersion.ts";

export const buildProductLiveScoreDimensions = (isMonolithic: boolean | undefined): Dimensions => {
    const productDimensions: Dimensions = [[["components", ["questionnaire"]]], [["components", ["product"]]]];

    if (!isMonolithic) {
        productDimensions.push([["components", ["ingredients"]], ["ingredients"]]);
        productDimensions.push([["components", ["transformations"]]]);
        productDimensions.push([["components", ["packagings"]], ["packagings"]]);
        productDimensions.push([["components", ["transport"]]]);
        productDimensions.push([["components", ["distribution"]], ["distribution"]]);
        productDimensions.push([["components", ["consumption"]], ["consumption"]]);
    }

    return productDimensions;
};

type ProductScoresContextProviderProps = {
    children: ReactNode;
    product: Product;
    ssq: SsqVersionSsq | undefined;
    isSsqReady: boolean;
    isActivePostLoading: boolean;
};
export const ProductScoresContextProvider = (props: ProductScoresContextProviderProps) => {
    const { ssq, isSsqReady } = props;
    const { setEntitySsq } = useEntityScoresOrUnavailable();

    useEffect(() => {
        setEntitySsq(ssq);
    }, [ssq]);

    if (isSsqReady === false) return <Loading />;

    return <ProductScoresContextProviderWithSsq {...props} />;
};

export const ProductScoresContextProviderWithSsq = (props: ProductScoresContextProviderProps) => {
    const { children, product, isActivePostLoading } = props;
    const { selectedCompany, currentCampaign } = useFoodpilotContext();
    const entityScores = useEntityScoresOrUnavailable();
    const { entitySsq } = entityScores;
    const lastSelectedVariation = entityScores.isAvailable ? entityScores.lastSelectedVariation : null;

    const productDimensions = buildProductLiveScoreDimensions(entitySsq?.monolithic);

    const productScoresParams = useOlapScoresParams({
        scores: {
            dimensions: productDimensions,
            variations: ScoreService.getVariationByLastSelectedVariation(lastSelectedVariation),
        },
        prevScores: true,
    });

    const {
        data: scores,
        error: scoreQueryError,
        isError: isScoreQueryError,
        isFetching: areScoresRefetching,
        refetch: retryScoreQuery,
    } = useGetProductScores({
        productId: Number(product.id),
        companyId: selectedCompany?.id,
        campaignId: currentCampaign?.id,
        scoresQuery: productScoresParams.query,
        enabled: lastSelectedVariation !== null && isActivePostLoading === false,
    });

    useEffect(() => {
        productScoresParams.state.setQuery({
            ...productScoresParams.query,
            scores: {
                ...productScoresParams.query.scores,
                variations: ScoreService.getVariationsByLastSelectedVariation(
                    productScoresParams.query.scores?.variations,
                    lastSelectedVariation,
                ),
            },
        });
    }, [lastSelectedVariation?.scoreId, lastSelectedVariation?.variationId]);

    const productSummary = useProductSummaryData({
        product,
        companyId: selectedCompany?.id,
        campaignId: currentCampaign?.id,
    });

    const items: ProductScoresContextInitialValues = {
        scores,
        areScoresRefetching,
        scoreQueryError,
        isScoreQueryError,
        retryScoreQuery,

        productSummary,
    };

    return <ProductScoresContext.Provider value={items}>{children}</ProductScoresContext.Provider>;
};
